import { defineMetrics } from "../types.js"

/**
 * ⚠ **srv has two sets of module names, and adoption rate divides one by the other.**
 */
const ENUM_CAVEAT =
  "UserBehaviorAnalysis.module is a seven-value enum (application / forms / tracking / DCT / CCP / " +
  "devicereg / pharmareg); DMS, Standards, Projects and Checklists are not instrumented yet. " +
  "Those modules emit no points at all — so they render as “No data” rather than as a zero — and are " +
  "excluded both from percentage denominators and from most/least-used rankings."

export const moduleUsageMetrics = defineMetrics([
  {
    id: "module_adoption_rate",
    name: "Module Adoption Rate",
    description: "Percentage of users with module access who actively use the module.",
    logic: "(Active Users ÷ Users with Access) × 100.",
    source: "userBehaviorAnalysis.module (numerator) ÷ user.permission (denominator), joined via an explicit name map",
    domain: "moduleUsage",
    srvModule: "moduleUsage",
    priority: "nice",
    valueType: "ratio",
    dims: ["module"],
    rollup: "avg",
    defaultWidget: "gauge",
    altWidgets: ["bar", "hbar", "pie"],
    coveredByV1: false,
    caveat: ENUM_CAVEAT,
    xlsxRow: 12,
  },
  {
    id: "total_time_per_module",
    name: "Total Time Spent per Module",
    description: "Total time users spend within a specific module.",
    logic:
      "Sum user session duration while interacting with the module - Time between enetring and leaving a module screen",
    source: "UserBehaviorAnalysis.totalTime",
    domain: "moduleUsage",
    srvModule: "moduleUsage",
    priority: "nice",
    valueType: "duration_sec",
    dims: ["module"],
    rollup: "sum",
    defaultWidget: "stacked-bar",
    altWidgets: ["bar", "hbar", "table"],
    coveredByV1: false,
    caveat: ENUM_CAVEAT,
    xlsxRow: 13,
  },
  {
    id: "avg_time_per_module",
    name: "Average Time Spent per Module",
    description: "Average time users spend within a specific module.",
    logic: "Total module usage time ÷ Number of active users.",
    domain: "moduleUsage",
    srvModule: "moduleUsage",
    priority: "must",
    valueType: "duration_sec",
    dims: ["module"],
    rollup: "avg",
    defaultWidget: "bar",
    altWidgets: ["hbar", "table"],
    coveredByV1: false,
    caveat: ENUM_CAVEAT,
    xlsxRow: 14,
  },
  {
    id: "last_module_activity",
    name: "Last Module Activity",
    description: "Most recent activity performed within a module.",
    logic: "Retrieve the latest  timestamp recorded for the module.",
    source: "UserBehaviorAnalysis.visitTime",
    domain: "moduleUsage",
    srvModule: "moduleUsage",
    priority: "must",
    valueType: "timestamp",
    dims: ["module", "user"],
    rollup: "max",
    defaultWidget: "table",
    coveredByV1: false,
    caveat: ENUM_CAVEAT,
    xlsxRow: 15,
  },
  {
    id: "most_used_modules",
    name: "Most Frequently Used Modules",
    description: "Modules with the highest user activity.",
    logic: "Rank modules by total  usage time.",
    source: "userBehaviorAnalysis.totalTime, ranked",
    domain: "moduleUsage",
    srvModule: "moduleUsage",
    priority: "nice",
    valueType: "count",
    dims: ["module"],
    rollup: "sum",
    defaultWidget: "hbar",
    altWidgets: ["bar", "table"],
    coveredByV1: false,
    caveat: ENUM_CAVEAT,
    xlsxRow: 83,
  },
  {
    id: "least_used_modules",
    name: "Least Frequently Used Modules",
    description: "Modules with the lowest user activity.",
    logic: "Rank modules by lowest usage time.",
    domain: "moduleUsage",
    srvModule: "moduleUsage",
    priority: "nice",
    valueType: "count",
    dims: ["module"],
    rollup: "sum",
    defaultWidget: "hbar",
    altWidgets: ["bar", "table"],
    coveredByV1: false,
    caveat: ENUM_CAVEAT,
    xlsxRow: 84,
  },
  {
    id: "module_growth",
    name: "Module Growth",
    description: "Growth in module records over time.",
    logic:
      "Compare record counts between reporting periods. uggested formula for the rate: ((Current period count − Prior period count) ÷ Prior period count) × 100.",
    domain: "moduleUsage",
    srvModule: "moduleUsage",
    priority: "nice",
    valueType: "count",
    dims: ["module"],
    rollup: "sum",
    defaultWidget: "line",
    altWidgets: ["bar", "stacked-bar"],
    coveredByV1: false,
    caveat: ENUM_CAVEAT,
    xlsxRow: 87,
  },
])
